import { Form, Modal, Input, Spin, notification, Button } from "antd";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import {
	updateCompanyUser,
	getCompanyUserById,
	grantMentorRights,
} from "../../../Requests/company-users-requests";
import { getErrorFromResponse } from "../../../utils/responseUtils";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../../Notifications/NotificationsUtils";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { CompanyUser, ErrorCodes, GrantMentorRequestDTO } from "../../../Api";
import CustomForm from "../../../CustomComponents/CustomForm";
import CustomEditor from "../../../CustomComponents/CustomEditor";

interface UpdateMentorInfoModalFormProps {
	userId: string;
	initialCnp?: string | null;
	initialMentorDescription?: string | null;
	isVisible: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

interface MentorFormModel {
	cnp: string;
	mentorDescription: string;
}

export const UpdateMentorInfoModalForm = ({
	userId,
	initialCnp,
	initialMentorDescription,
	isVisible,
	onClose,
	onSuccess,
}: UpdateMentorInfoModalFormProps) => {
	const { t } = useTranslation();
	const [form] = Form.useForm<MentorFormModel>();
	const queryClient = useQueryClient();

	const {
		data: userData,
		isLoading: isUserLoading,
		isError: isUserError,
		error: userError,
	} = useQuery<CompanyUser, Error>(
		["companyUser", userId],
		() => getCompanyUserById(userId),
		{
			enabled: isVisible && !!userId,
			staleTime: Infinity, // Data won't refetch on subsequent renders unless explicitly invalidated
			cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
			onSuccess: (data) => {
				form.setFieldsValue({
					cnp: data.cnp || initialCnp || "",
					mentorDescription:
						data.mentorDescription || initialMentorDescription || "",
				});
			},
			onError: async (error: any) => {
				const errorMessage = await getErrorFromResponse(error);
				openNotification(
					t("account.error"),
					errorMessage?.message || t("usersText.errorTexts.failedUserGet"),
					NOTIFICATION_TYPES.ERROR
				);
				onClose();
			},
		}
	);

	const updateMentorMutation = useMutation(
		(payload: GrantMentorRequestDTO) => grantMentorRights(payload),
		{
			onSuccess: () => {
				openNotification(
					t("account.companyMentorNominated"),
					t("account.companyMentorNominatedMessage"),
					NOTIFICATION_TYPES.SUCCESS
				);
				queryClient.invalidateQueries(["companyUser", userId]);
				onSuccess();
				onClose();
			},
			onError: async (error: any) => {
				const errorMessage = await getErrorFromResponse(error);
				let notificationMessage;

				switch (errorMessage?.code) {
					case ErrorCodes.Forbidden:
						notificationMessage = t("errors.forbidden");
						break;
					case ErrorCodes.UserNotFound:
						notificationMessage = t("account.notFoundUser");
						break;
					default:
						notificationMessage = t("account.unknownError");
						break;
				}

				openNotification(
					t("account.error"),
					notificationMessage,
					NOTIFICATION_TYPES.ERROR
				);
			},
		}
	);

	const schema = yup.object().shape({
		cnp: yup
			.string()
			.trim()
			.required(t("account.requiredField", { field: t("account.cnp") })),
		mentorDescription: yup
			.string()
			.trim()
			.required(
				t("account.requiredField", { field: t("account.mentorDescription") })
			),
	});

	const validator = useCallback(
		async ({ field }: any, _value: any) => {
			await schema.validateSyncAt(field, form.getFieldsValue());
		},
		[schema, form]
	);

	const yupSync = {
		validator,
	};

	useEffect(() => {
		if (!isVisible) {
			form.resetFields();
		}
	}, [isVisible, form]);

	const handleFormSubmit = async (values: MentorFormModel) => {
		if (!userData) {
			openNotification(
				t("account.error"),
				t("usersText.errorTexts.failedUserGet"),
				NOTIFICATION_TYPES.ERROR
			);
			return;
		}

		const payload: GrantMentorRequestDTO = {
			userId: userId,
			cnp: values.cnp,
			mentorDescription: values.mentorDescription,
		};

		updateMentorMutation.mutate(payload);
	};

	const isSubmitting = updateMentorMutation.isLoading || isUserLoading;

	return (
		<Modal
			title={t("account.grantMentorRights")}
			open={isVisible}
			onCancel={onClose}
			footer={[
				<Button key="back" onClick={onClose} disabled={isSubmitting}>
					{t("common.cancel")}
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={isSubmitting}
					onClick={() => form.submit()}
				>
					{t("common.save")}
				</Button>,
			]}
		>
			<Spin spinning={isSubmitting}>
				<CustomForm layout="vertical" form={form} onFinish={handleFormSubmit}>
					<Form.Item name="id" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="name" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="email" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="phone" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="_function" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="department" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="isRepresentative" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="isAdmin" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="isMentor" hidden>
						<Input />
					</Form.Item>
					<Form.Item name="sendNotifications" hidden>
						<Input />
					</Form.Item>
					{t("account.mentorInfo")}

					<Form.Item
						name="cnp"
						label={`${t("account.cnp")}:`}
						rules={[yupSync]}
						required
						style={{ width: "90%" }}
					>
						<Input placeholder={t("account.cnp")} />
					</Form.Item>

					<Form.Item
						name="mentorDescription"
						label={`${t("account.mentorDescription")}:`}
						rules={[
							{
								whitespace: true,
							},
						]}
						required
						style={{ width: "90%" }}
					>
						<CustomEditor
							onEditorChange={(content: string) => {
								form.setFieldsValue({ mentorDescription: content });
							}}
						/>
					</Form.Item>
				</CustomForm>
			</Spin>
		</Modal>
	);
};
